import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, getCategoryBySlug, getCategoryNameBySlug } from '@/mocks/categories';
import { getComicsByCategory, comics as allComics } from '@/mocks/comics';

const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const categoryName = getCategoryNameBySlug(slug || '');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const comicsList = useMemo(() => {
    let list = slug ? getComicsByCategory(slug) : allComics;
    if (sortBy === 'popular') {
      return [...list].sort((a, b) => b.views - a.views);
    }
    if (sortBy === 'rating') {
      return [...list].sort((a, b) => b.rating - a.rating);
    }
    return [...list].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [slug, sortBy]);

  const seoTitle = `${categoryName} - 韩国${categoryName}免费在线观看 | 汗汗漫画`;
  const seoDesc = `汗汗漫画${categoryName}专区，免费在线观看最新最热门的韩国${categoryName}，流畅下拉式阅读，每日更新热门韩漫，好看的${categoryName}尽在汗汗漫画。`;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: seoTitle,
    description: seoDesc,
    url: `https://hanhanmanhua.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '汗汗漫画',
      url: 'https://hanhanmanhua.com/',
    },
  };

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={`汗汗漫画,${categoryName},韩国漫画,韩漫,免费在线观看,下拉式阅读`}
        canonicalUrl={`https://hanhanmanhua.com/category/${slug}`}
        structuredData={structuredData}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Breadcrumb + Header */}
          <div className="bg-background-50 border-b border-secondary-200/40">
            <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName },
                ]}
              />
              <div className="py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                    {categoryName}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    共 {comicsList.length} 部漫画
                  </p>
                </div>
                {/* Sort */}
                <div className="flex items-center gap-1 bg-secondary-100 rounded-full p-1">
                  {[
                    { key: 'latest', label: '最新' },
                    { key: 'popular', label: '最热' },
                    { key: 'rating', label: '评分' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key as typeof sortBy)}
                      className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all duration-200 ${
                        sortBy === opt.key
                          ? 'bg-background-50 text-foreground-950 font-medium'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="bg-background-50 border-b border-secondary-200/40">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 overflow-x-auto py-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Comics Grid */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {comicsList.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {comicsList.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-foreground-400">
                    <i className="ri-emotion-sad-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无漫画</p>
                  <Link
                    to="/"
                    className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    返回首页
                  </Link>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CategoryPage;